import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface ChildLink {
  label: string;
  href: string;
}

interface NavItem {
  label: string;
  href: string;
  children?: ChildLink[];
}

const navItems: NavItem[] = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services' },
  { label: 'About', href: '/about' },
  { label: 'Locations', href: '/locations' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();

  const isHomepage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const isDark = isHomepage && !scrolled;

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16 lg:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center cursor-pointer">
          <img
            src="https://storage.readdy-site.link/project_files/82416851-1135-4bfe-8ce6-550ef563351f/b3853fc3-6fd9-4dd8-b884-cccfadedee77_MyStudio_Logo_Studio.png?v=cab6c10ee6061b438c10513746d93a4d"
            alt="Book My Studio Space"
            className={`h-10 w-auto object-contain transition-all ${isDark ? 'brightness-0 invert' : ''}`}
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-7">
          {navItems.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => item.children && setActiveDropdown(item.label)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              {item.children ? (
                <button
                  className={`flex items-center gap-0.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isDark ? 'text-white/85 hover:text-white' : 'text-stone-600 hover:text-amber-600'
                  }`}
                >
                  {item.label}
                  <i className="ri-arrow-down-s-line text-sm mt-0.5" />
                </button>
              ) : (
                <Link
                  to={item.href}
                  className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isDark ? 'text-white/85 hover:text-white' : 'text-stone-600 hover:text-amber-600'
                  }`}
                >
                  {item.label}
                </Link>
              )}

              {item.children && activeDropdown === item.label && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 bg-white rounded-xl shadow-xl border border-stone-100 py-2 z-50">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-2 overflow-hidden">
                    <div className="w-3 h-3 bg-white border-l border-t border-stone-100 rotate-45 mx-auto mt-1" />
                  </div>
                  {item.children.map((child) => (
                    <Link
                      key={child.href}
                      to={child.href}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-stone-700 hover:text-amber-600 hover:bg-amber-50 transition-colors cursor-pointer"
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-5">
          <a
            href="tel:+18005551234"
            className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
              isDark ? 'text-white/85 hover:text-white' : 'text-stone-600 hover:text-stone-900'
            }`}
          >
            <i className="ri-phone-line" />
            (800) 555-1234
          </a>
          <button
            onClick={() => scrollToSection('book')}
            className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
          >
            Book Now
          </button>
        </div>

        {/* Mobile Toggle */}
        <button
          className={`lg:hidden w-9 h-9 flex items-center justify-center cursor-pointer rounded-md transition-colors ${
            isDark ? 'text-white hover:bg-white/10' : 'text-stone-900 hover:bg-stone-100'
          }`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-stone-100 px-6 py-5">
          {navItems.map((item) => (
            <div key={item.label} className="mb-3">
              {item.children ? (
                <div>
                  <p className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-2">{item.label}</p>
                  {item.children.map((child) => (
                    <Link
                      key={child.href}
                      to={child.href}
                      className="block pl-2 py-2 text-sm text-stone-700 hover:text-amber-600 cursor-pointer border-l-2 border-stone-100 hover:border-amber-400 transition-colors mb-1"
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  to={item.href}
                  className="block py-2.5 text-sm font-semibold text-stone-900 hover:text-amber-600 cursor-pointer"
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}
          <div className="mt-4 pt-4 border-t border-stone-100 flex flex-col gap-3">
            <a
              href="tel:+18005551234"
              className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer"
            >
              <i className="ri-phone-line text-amber-500" />
              (800) 555-1234
            </a>
            <button
              onClick={() => scrollToSection('book')}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold py-3 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              Book Now
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
